import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import moment from "moment";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { DataTypeProvider } from "@devexpress/dx-react-grid";
import {
  Grid,
  TableFixedColumns,
  TableHeaderRow,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { mergeBudgetAndActualSpendToForecastInput } from "@features/mmm/utils/budget-mix-forecast-input-helpers";
import { type ForecastVariables } from "@north-beam/nb-client-side";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";

import {
  useEditableCells,
  type EditedCell,
} from "../../hooks/use-editable-cells";

import { TableCell, TableRow } from "./body-components";
import {
  COLUMNS,
  DEFAULT_COLUMN_WIDTHS,
  FIXED_NAME_COLUMN_ID,
  PACING_STATUS_COLUMN_ID,
  ROW_HEIGHT,
  SPEND_THERMOMETER_ID,
  TOTAL_BUDGET_COLUMN_ID,
  TOTAL_BUDGET_PROPOSED_COLUMN_ID,
} from "./constants";
import {
  SpendChannelNameCellFormatter,
  SpendThermometerDataFormatter,
  PacingStatusDataFormatter,
  TotalBudgetDataFormatter,
  ProposedBudgetDataFormatter,
} from "./data-type-formatters";
import { FixedRow } from "./fixed-row-components";
import { TableHeaderCell, TableHeaderContent } from "./header-components";

import "../mmm-budget-pacing-table.scss";

export enum WeekStatus {
  COMPLETE = "Complete",
  CURRENT = "Current",
  UPCOMING = "Upcoming",
}

export type WeekData = {
  actual: number | null;
  budgeted: number | null;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  status: WeekStatus;
};

export type TableRowTypeData = {
  weeklyMetrics: WeekData[];
  actualSpend: number;
  actualSpendMix: number;
  budgetedSpendToDate: number;
  budgetedSpend: number;
  proposedBudgetedSpend: number;
  budgetedSpendMix: number;
  mtaRevenue: number;
  dataColumnName: string;
  mtaTransactions: number;
  mtaRoas: number;
  mtaCac: number;
};
export type TableRowTypeMetaData = {
  breakdowns: Record<string, string>;
  currentBreakdown: { key: string; value: string };
  firstBreakdown: string;
  hasAlert: boolean;
};
export type RebudgetingTableRow = {
  id: string;
  key: string;
  isHeaderRow?: boolean;
  index: number;
  data: TableRowTypeData;
  displayName: string;
  children: RebudgetingTableRow[];
  metadata: TableRowTypeMetaData;
  hasChildren: boolean;
  rowType: "normal";
};

export type LoadingRowType = {
  id: string;
  key: string;
  rowType: "loading";
};

export type RebudgetingOrLoadingTableRow = RebudgetingTableRow | LoadingRowType;

interface Props {
  rows: RebudgetingTableRow[];
  setRows: (rows: RebudgetingTableRow[]) => void;
  loading: boolean;
  headingRow: RebudgetingOrLoadingTableRow | undefined;
  getForecast: (variables: ForecastVariables) => Promise<unknown>;
  allowForecastAdoption: boolean;
  onAdoptForecast: () => Promise<void>;
  forecastError: boolean;
}

interface ExtendedCSSStyleDeclaration extends CSSStyleDeclaration {
  "scrollbar-width": string;
}

const DUMMY_ROW_ID = "dummy";

export const MidCycleRebudgetingTable = ({
  rows,
  setRows,
  loading,
  headingRow,
  getForecast,
  allowForecastAdoption,
  onAdoptForecast,
  forecastError,
}: Props) => {
  const { selectedCycle } = useSelectedCycle();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);

  const [shouldCommitChanges, setShouldCommitChanges] =
    useState<boolean>(false);
  const [rowsSynced, setRowsSynced] = useState(true);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [loadingAdoption, setLoadingAdoption] = useState(false);

  const { editedCells, commitChanges, previewCommit } = useEditableCells(
    rows,
    (row: RebudgetingTableRow, columnName: string) => {
      if (columnName === TOTAL_BUDGET_PROPOSED_COLUMN_ID) {
        return row.data.proposedBudgetedSpend;
      }
      // Not used
      return 0;
    },
    (row: RebudgetingTableRow, cell: EditedCell<number>) => {
      row.data.proposedBudgetedSpend = cell.value;
    },
    (newRows: RebudgetingTableRow[]) => {
      setRows(newRows);
    },
  );

  // HACK for scrolling above footer component
  const dummyRow: RebudgetingTableRow = useMemo(
    () =>
      ({
        id: DUMMY_ROW_ID,
        key: DUMMY_ROW_ID,
        isHeaderRow: false,
        index: rows.length,
        data: {},
        displayName: "",
        metadata: {},
        hasChildren: false,
        rowType: "dummy",
      } as unknown as RebudgetingTableRow),
    [rows.length],
  );

  const rowsToRender = useMemo(() => {
    if (loading) {
      return Array.from({ length: 15 }).map((_, index) => ({
        key: `loading-${index}`,
        id: `loading-${index}`,
        rowType: "loading",
        index,
      }));
    } else {
      return [...rows, dummyRow];
    }
  }, [loading, rows, dummyRow]);

  useEffect(() => {
    if (shouldCommitChanges) {
      commitChanges();
      setShouldCommitChanges(false);
      trackMMMEvent({
        component:
          TrackPerformanceTrackedComponent.ALTER_BUDGET_REBUDGETING_TABLE,
        action: "onChange",
        actionInfo: "Commit changes into proposed budget column",
      });
    }
  }, [
    shouldCommitChanges,
    setShouldCommitChanges,
    commitChanges,
    trackMMMEvent,
  ]);

  useEffect(() => {
    setRowsSynced(true);
  }, [rows]);

  useEffect(() => {
    if (editedCells.length > 0) {
      setRowsSynced(false);
    }
  }, [editedCells.length]);

  const gridRef = useRef<HTMLDivElement>(null);

  // this gets the aggregate values
  const FixedTopRow = useMemo(
    () => FixedRow(headingRow as RebudgetingTableRow, rows),
    [headingRow, rows],
  );

  const handleAdoptForecast = useCallback(async () => {
    trackMMMEvent({
      component:
        TrackPerformanceTrackedComponent.ADOPT_MID_CYCLE_BUDGET_REALLOCATION_FORECAST,
      action: "onClick",
      actionInfo: "Adopt forecast",
    });
    setLoadingAdoption(true);
    await onAdoptForecast();
    setLoadingAdoption(false);
  }, [trackMMMEvent, onAdoptForecast]);

  const handleForecastClick = useCallback(async () => {
    const updatedRows = previewCommit();
    const proposedCycleSpendByDataColumnName = updatedRows.reduce(
      (acc, row) => {
        const { dataColumnName, proposedBudgetedSpend } = row.data;
        acc[dataColumnName] = proposedBudgetedSpend;
        return acc;
      },
      {} as Record<string, number>,
    );

    const actualSpendByDatacolumnName = updatedRows.reduce((acc, row) => {
      acc[row.data.dataColumnName] = row.data.weeklyMetrics.map(
        (weeklyMetric) => ({
          ...weeklyMetric,
          actual: weeklyMetric.actual ?? 0,
        }),
      );
      return acc;
    }, {} as { [DataColumnName: string]: { actual: number; dateRange: { startDate: string; endDate: string } }[] });

    const forecastInputData = mergeBudgetAndActualSpendToForecastInput(
      proposedCycleSpendByDataColumnName,
      actualSpendByDatacolumnName,
      moment().format("YYYY-MM-DD"),
      selectedCycle.date_range,
    );

    const forecastInput: ForecastVariables = {
      input: {
        modelPath: selectedCycle.model.model_path,
        modelId: selectedCycle.model.create_northbeam_mmm_run_result,
        data: forecastInputData,
      },
    };

    trackMMMEvent({
      component:
        TrackPerformanceTrackedComponent.FORECAST_MID_CYCLE_BUDGET_REALLOCATION,
      action: "onClick",
      actionInfo: "Forecast mid-cycle budget reallocation",
    });

    setLoadingForecast(true);
    await getForecast(forecastInput);
    commitChanges();
    setLoadingForecast(false);
  }, [
    previewCommit,
    selectedCycle.date_range,
    selectedCycle.model.model_path,
    selectedCycle.model.create_northbeam_mmm_run_result,
    getForecast,
    commitChanges,
    trackMMMEvent,
  ]);

  // NOTE: Hack to remove scrollbar inside of grid
  useEffect(() => {
    if (gridRef.current) {
      const scrollableElement = gridRef.current.querySelector(
        "div.table-responsive",
      ) as HTMLElement;
      if (scrollableElement) {
        (scrollableElement.style as ExtendedCSSStyleDeclaration)[
          "scrollbar-width"
        ] = "none";
      }
    }
  });

  const TableGrid = useMemo(() => {
    return (
      <div
        className="mmm-budget-pacing-table h-[580px] rounded-md relative w-full min-w-full"
        ref={gridRef}
      >
        <Grid columns={COLUMNS} rows={rowsToRender} getRowId={(row) => row.key}>
          {/* Custom column cells */}
          <DataTypeProvider
            for={[FIXED_NAME_COLUMN_ID]}
            formatterComponent={SpendChannelNameCellFormatter}
          />
          <DataTypeProvider
            for={[PACING_STATUS_COLUMN_ID]}
            formatterComponent={PacingStatusDataFormatter}
          />
          <DataTypeProvider
            for={[SPEND_THERMOMETER_ID]}
            formatterComponent={SpendThermometerDataFormatter}
          />
          <DataTypeProvider
            for={[TOTAL_BUDGET_COLUMN_ID]}
            formatterComponent={TotalBudgetDataFormatter}
          />
          <DataTypeProvider
            for={[TOTAL_BUDGET_PROPOSED_COLUMN_ID]}
            formatterComponent={(props) => (
              <ProposedBudgetDataFormatter {...props} onBlur={() => {}} />
            )}
          />
          <VirtualTable
            height="auto"
            estimatedRowHeight={ROW_HEIGHT}
            columnExtensions={DEFAULT_COLUMN_WIDTHS}
            headComponent={FixedTopRow as React.ComponentType<object>}
            rowComponent={(props) => {
              const row = props.row;
              if (row.rowType === "loading") {
                return (
                  <motion.tr
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={clsx(
                      props.row.index % 2 === 0 ? "even" : "odd",
                      "relative",
                    )}
                    style={{ height: ROW_HEIGHT }}
                  >
                    <td
                      className="sticky bg-v2-primary z-1 border-b border-v2-secondary"
                      style={{ left: "0px" }}
                    >
                      <div className="flex items-center h-full ml-2">
                        <img
                          src={salesPlatformColumnLoading}
                          alt="Loading table"
                          style={{
                            height: "2rem",
                          }}
                        />
                      </div>
                    </td>
                    {Array.from({ length: COLUMNS.length }).map((_, i) => (
                      <td key={i} className="border-b border-v2-secondary">
                        <div className="animate-pulse flex-grow h-full flex items-center">
                          <div className="ml-2 h-2 w-2/5 bg-gray-2"></div>
                        </div>
                      </td>
                    ))}
                  </motion.tr>
                );
              } else if (row.rowType === "dummy") {
                return <div className="h-10"></div>;
              } else {
                return <TableRow {...props}></TableRow>;
              }
            }}
            cellComponent={TableCell}
          />
          <TableHeaderRow
            cellComponent={TableHeaderCell}
            contentComponent={TableHeaderContent}
          />
          <TableFixedColumns leftColumns={[FIXED_NAME_COLUMN_ID]} />
        </Grid>
      </div>
    );
  }, [rowsToRender, FixedTopRow]);

  return (
    <div className="relative rounded-md">
      <div>{TableGrid}</div>
      <div className="absolute bottom-0 left-0 w-full z-100 flex justify-between p-4 pb-8 backdrop-blur-sm h-10 backdrop-brightness-105">
        <div>
          <TertiaryButton onClick={() => {}} hidden>
            Cancel
          </TertiaryButton>
        </div>
        <div className="flex items-end flex-col">
          <div className="flex justify-start gap-2">
            <SecondaryButton
              onClick={handleForecastClick}
              loading={loadingForecast}
              disabled={!forecastError && (loading || editedCells.length === 0)}
            >
              Forecast
            </SecondaryButton>
            {/* TODO: Add tooltip if disabled to forecast first */}
            <PrimaryButton
              disabled={
                !allowForecastAdoption ||
                editedCells.length !== 0 ||
                forecastError
              }
              onClick={handleAdoptForecast}
              loading={loadingAdoption}
              className={forecastError ? "bg-mars" : undefined}
            >
              Adopt
            </PrimaryButton>
          </div>
          {forecastError && (
            <span className="text-mars text-xxs font-bold block">
              There was an error generating a budget for your forecast, please
              try again
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
